/**
 * Kakao OAuth 관련 핸들러 함수들
 */

import axios from 'axios';
import type { User } from '@edusense/shared-types-user';
import type { AuthResponse, TOKEN_KEY, REFRESH_TOKEN_KEY, ID_TOKEN_KEY } from './types';

export interface OAuthHandlersConfig {
  tokenManager: { setToken: (token: string) => void };
  apiService: { setToken: (token: string) => void };
  isVSCodeEnv: boolean;
  fetchUserProfile: (token: string) => Promise<void>;
  setAuthenticatedState?: (
    user: User, 
    accessToken: string, 
    refreshToken?: string, 
    expiresIn?: number,
    tokenType?: string,
    idToken?: string
  ) => Promise<void>;
}

/**
 * 카카오 Authorization Code 교환 함수
 */
export const createKakaoAuthCodeExchange = (config: OAuthHandlersConfig) =>
  async (authCode: string): Promise<AuthResponse> => {
    console.log('[OAuth] Exchanging Kakao authorization code');

    try {
      const tokenEndpoint = (import.meta.env.VITE_AUTH_SERVER_URL || 'https://auth.ugot.uk') + '/oauth2/token';

      const params = new URLSearchParams({
        grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
        client_id: 'app-client',
        subject_token: authCode,
        subject_token_type: 'urn:x-oauth:params:token-type:authorization-code',
        actor_token: 'kakao',
        scope: 'openid profile email api.read'
      });

      const response = await axios.post(tokenEndpoint, params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json'
        },
        withCredentials: true,
        maxRedirects: 0,
        validateStatus: (status) => status >= 200 && status < 400
      });

      // 응답이 배열 형태인 경우 처리
      let tokenData = response.data;
      if (Array.isArray(response.data) && response.data.length === 2 && response.data[0] === 'java.util.LinkedHashMap') {
        tokenData = response.data[1];
      }

      if (tokenData && tokenData.access_token) {
        const { access_token, refresh_token, id_token, expires_in } = tokenData;

        // 토큰 저장
        config.tokenManager.setToken(access_token);
        config.apiService.setToken(access_token);

        if (!config.isVSCodeEnv) {
          localStorage.setItem(TOKEN_KEY, access_token);
          if (refresh_token) {
            localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
          }
          if (id_token) {
            localStorage.setItem(ID_TOKEN_KEY, id_token);
          }
        }

        // 사용자 프로필 가져오기
        await config.fetchUserProfile(access_token);

        // setAuthenticatedState가 제공되고 웹 환경인 경우, ID 토큰과 함께 상태 업데이트
        if (config.setAuthenticatedState && !config.isVSCodeEnv) {
          const storedUser = localStorage.getItem('edusense_auth_user');
          if (storedUser) {
            try {
              const user = JSON.parse(storedUser);
              console.log('[OAuth] Kakao Auth Code - setAuthenticatedState 호출 with ID token:', !!id_token);
              await config.setAuthenticatedState(
                user, 
                access_token, 
                refresh_token, 
                expires_in, 
                'Bearer', 
                id_token // ID 토큰을 포함하여 전달
              );
            } catch (error) {
              console.error('[OAuth] Kakao Auth Code - setAuthenticatedState 호출 실패:', error);
            }
          }
        }

        return {
          success: true,
          data: {
            access_token,
            refresh_token,
            id_token,
            expires_in
          }
        };
      } else {
        console.error('[OAuth] Token exchange response missing access_token:', response.data);
        return {
          success: false,
          message: tokenData?.error_description || tokenData?.error || '토큰 교환에 실패했습니다.'
        };
      }
    } catch (error: any) {
      console.error('[OAuth] Kakao auth code exchange failed:', error);
      return {
        success: false,
        message: error.response?.data?.error_description || error.message || '카카오 로그인 중 오류가 발생했습니다.'
      };
    }
  };